import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import {
  DEFAULT_FORECAST_LIST,
  FORECAST_CLEANING_KINDS,
  FORECAST_GOAL_KEYS,
  FORECAST_STATUS_KEYS,
  FORECAST_UNSCHEDULED_REASONS,
  FORECAST_WORK_KINDS,
  PLANNER_GROUP_KEYS,
  SCHEDULED_GROUP_KEYS,
  UNSCHEDULED_GROUP_KEYS,
  forecastsSearchSchema,
  listParamFor,
  parseKnownCsv,
  stageIdsFromParam,
  stageParamFromIds,
} from '../-components/forecasts-search-schema'
import { effectiveCleaningSince } from '../-components/cleaning-since'
import { ForecastOffsetsHint } from '../-components/ForecastOffsetsHint'
import { AwaitingNextMeetingSection } from '../-components/AwaitingNextMeetingSection'
import { UpcomingDeliveryWorkSection } from '../-components/UpcomingDeliveryWorkSection'
import { ReadyToCloseSection } from '../-components/ReadyToCloseSection'
import { PostDeliveryWorkSection } from '../-components/PostDeliveryWorkSection'
import { ForecastQueueSection } from '../-components/ForecastQueueSection'
import { ForecastGroupChips } from '../-components/ForecastGroupChips'
import { ForecastStatusCards } from '../-components/ForecastStatusCards'
import { ForecastGoalBars } from '../-components/ForecastGoalBars'
import { ForecastStagePills } from '../-components/ForecastStagePills'
import { ForecastFiltersPopover } from '../-components/ForecastFiltersPopover'
import { ForecastListNavigation } from '../-components/ForecastListNavigation'
import { StatusPillDeadEndState } from '../-components/StatusPillDeadEnd'
import { NeedsCleanedSection } from '../-components/NeedsCleanedSection'
import { AlertsViewSection } from '../-components/AlertsViewSection'
import { SelectionViewSection } from '../-components/SelectionViewSection'
import { ForecastReferenceTab } from '../-components/ForecastReferenceTab'
import {
  LIST_TABS,
  LIST_TAB_LABELS,
  SELECTION_SCOPE,
  effectiveRowFilters,
  filterAppliesOn,
  filterClearHint,
  forecastViewMode,
  hasActiveRowFilters,
  showsAlertsTable,
  showsListTabs,
} from '../-components/forecast-list-tabs'
import { statusPillCounts } from '../-components/forecast-status-pills'
import { forecastGoalReadings } from '../-components/forecast-goals'
import {
  goalDeadEnd,
  goalFilterAppliesOn,
  goalSelectionLabel,
  narrowQueueRowsByGoals,
  toggleGoalFilter,
} from '../-components/forecast-goal-filter'
import {
  CASE_DESIGN_STAGES,
  narrowRowsByStage,
  stagePillCounts,
  stagePillsNarrow,
} from '../-components/forecast-stage-pills'
import {
  keepsDeliveredRows,
  narrowQueueRowsByPills,
  statusPillDeadEnd,
  statusPillsApplyOn,
  toggleStatusPill,
} from '../-components/forecast-status-pill-filter'
import { buildAlertsViewRows } from '../-components/alerts-view-rows'
import {
  buildSelectionViewRows,
  hasSelection,
  isSelectionViewTab,
  selectionDeadEnd,
  selectionHeading,
  selectionTabCounts,
  selectionViewActive,
} from '../-components/forecast-selection-view'
import {
  PLANNER_GROUPS,
  SCHEDULED_GROUPS,
  UNSCHEDULED_GROUPS,
  groupCounts,
  plannerGroupFor,
  scheduledGroupFor,
  unscheduledGroupFor,
} from '../-components/forecast-queue-groups'
import {
  queuePopulations,
  rowsByGroup,
  toggleGroupKey,
} from '../-components/forecast-work-tabs'
import {
  DEFAULT_FORECAST_QUEUE_ROW_FILTERS,
  filterForecastLists,
} from '../-components/forecast-queue-filters'
import {
  UNSCHEDULED_REASON_LABELS,
  caseBudgetBusinessDays,
  requiredReadyBusinessDays,
} from '../-components/forecast-queue-format'
import type { ForecastsSearch } from '../-components/forecasts-search-schema'
import type { SelectionViewTab } from '../-components/forecast-selection-view'
import type { ForecastGoalKey } from '../-components/forecast-goals'
import type { ForecastStatusPillKey } from '../-components/forecast-status-pills'
import type {
  PlannerGroupKey,
  ScheduledGroupKey,
  UnscheduledGroupKey,
} from '../-components/forecast-queue-groups'
import type { ForecastQueueSectionGroup } from '../-components/ForecastQueueSection'
import type { RollupExperienceType } from '@/components/shared/ExperienceRollupCard'
import type {
  CaseWorkKind,
  CleaningFindingKind,
  ForecastQueueFilters,
  ForecastQueueRow,
  UnscheduledReason,
} from '@/types/backofficeAnalytics.types'
import { PageTitle } from '@/components/PageTitle'
import {
  Tabs,
  TabsContent,
} from '@/components/animate-ui/components/radix/tabs'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { InfoHint } from '@/components/shared/InfoHint'
import { MultiSelectFilter } from '@/components/shared/MultiSelectFilter'
import { UserPicker } from '@/components/shared/UserPicker'
import {
  ANALYTICS_EXPERIENCE_LABELS,
  ANALYTICS_WORK_KIND_LABELS,
} from '@/types/backofficeAnalytics.types'
import {
  useBackofficeAnalyticsForecastQueue,
  useSyncCaseSchedule,
} from '@/hooks/repositories/useBackofficeAnalyticsRepository'
import { useMinimumPendingDuration } from '@/hooks/use-cases/useMinimumPendingDuration'
import { useCleaningFindings } from '@/hooks/use-cases/useCleaningFindings'
import { useDebouncedUrlText } from '@/hooks/use-cases/useDebouncedUrlText'
import { parseCsvParam, serializeCsvParam } from '@/lib/csv-search-param'
import { formatDateTime } from '@/lib/formatters'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

const EXPERIENCE_OPTIONS = (
  Object.entries(ANALYTICS_EXPERIENCE_LABELS) as Array<
    [RollupExperienceType, string]
  >
).map(([value, label]) => ({ value, label }))

/** `Case kind` filter options (sc-1212 round 3) — plain words, not the wire
 * enum, matching `ChangeRequestBadge`'s copy. */

/**
 * `Work kind` filter options (sc-1212 round 5) — built from
 * `ANALYTICS_WORK_KIND_LABELS` via `Object.entries`, same convention as
 * `EXPERIENCE_OPTIONS`, never a hand-written array.
 *
 * **`ANNUAL_REVIEW` is deliberately excluded.** `includeAnnualReviews`
 * already defaults to hiding those rows and returns early in
 * `matchesForecastQueueRowFilters` — offering the kind here too would give
 * two controls that disagree about the same rows, and selecting only
 * `ANNUAL_REVIEW` would be guaranteed empty.
 */
const CASE_WORK_KIND_OPTIONS = (
  Object.entries(ANALYTICS_WORK_KIND_LABELS) as Array<[CaseWorkKind, string]>
)
  .filter(([kind]) => kind !== 'ANNUAL_REVIEW')
  .map(([value, label]) => ({ value, label }))

/**
 * `No meeting booked` filter options (sc-1212 round 7) — the three reasons a
 * case has no delivery meeting, offered separately because only one of them is
 * a problem. Built from `UNSCHEDULED_REASON_LABELS` so a reason cannot be
 * offered here without having copy and a tone, which is how the previous
 * single "No meeting booked" lane ended up meaning three different things.
 *
 * Ordered by urgency, matching the API's `UnscheduledReason` declaration, so
 * the one a manager acts on is first rather than buried under the 79-row
 * normal bucket.
 */
const UNSCHEDULED_REASONS_IN_URGENCY_ORDER = [
  'DELIVERED_NOTHING_BOOKED',
  'CANCELLED_NOT_REBOOKED',
  'NEVER_BOOKED',
  // Pillars half of an SO sale, queued behind the Rothology half (round 23).
  // No action either, so it sits with KICKOFF_BOOKED at the bottom. Ahead of
  // it only because "waiting on another case to finish" is less self-evidently
  // fine to a reader than "the kickoff is already booked".
  //
  // It is also the SMALLEST bucket on the page: measured on production
  // 2026-10-19, the firm has 5 SO-tokened Case Design tickets, 2 of them
  // Pillars, and one of those is closed -- so exactly ONE open case reports
  // this today. Ordering it high would be ordering an empty list.
  'AWAITING_SECOND_SERVICE',
  // LAST, because it is the only one that needs no action (sc-1212 round 9) —
  // an IE case whose kickoff is already booked. Offered as a filter so a
  // manager can pull those out of the no-meeting rows deliberately rather
  // than wondering why a booked case is sitting among the unbooked ones.
  'KICKOFF_BOOKED',
] as const satisfies ReadonlyArray<Exclude<UnscheduledReason, null>>

const UNSCHEDULED_REASON_OPTIONS = UNSCHEDULED_REASONS_IN_URGENCY_ORDER.map(
  (reason) => ({ value: reason, label: UNSCHEDULED_REASON_LABELS[reason] }),
)

/**
 * A constant, empty filter object passed to the queue hook (sc-1212 round
 * 2). The queue is a standing snapshot of ~175 rows — small enough to filter
 * entirely client-side — so this page fetches it ONCE and does every filter
 * (search, band, experience, owner, Annual Review) in the browser via
 * `matchesForecastQueueRowFilters` below.
 *
 * **Do not put filter state back into this object or the query key.** The
 * API's `experience`/`person` params still work — nothing there changed —
 * but restoring them here silently brings back a round trip on every filter
 * change, which is the exact "archaic" behavior this round of work removed.
 * Declared as a module-level constant (not inline) so its identity never
 * changes and can't accidentally widen the query key either.
 */
const EMPTY_QUEUE_FILTERS: ForecastQueueFilters = { experience: [], person: [] }

/**
 * The sync endpoint only enqueues a BullMQ job — it answers in ~100ms, so a
 * spinner bound straight to the mutation's own `isPending` would render and
 * vanish before a press could register as acknowledged (Tyler, production:
 * "no processing animation just a toast"). This floors how long the button
 * stays visibly busy; see `useMinimumPendingDuration`'s doc comment.
 */
const SYNC_BUTTON_MIN_BUSY_MS = 1300

export const Route = createFileRoute('/_app/backoffice/analytics/forecasts/')({
  component: RouteComponent,
  // The console's own search schema EXTENDED with this page's view state
  // (sc-1212), so a `Link` either direction round-trips the WHOLE console
  // search state while the view itself is shareable and survives a refresh.
  // The console keeps the base schema, which strips these keys on the way
  // back — see the schema module's own doc comment, and why the list param
  // is `list` and never `tab`.
  validateSearch: forecastsSearchSchema,
  // Same guard as the console itself — `backoffice.analytics`, not a
  // department check. This is a sub-view of the console, so whoever can open
  // one can open the other.
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.analytics),
})

function RouteComponent() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // URL-backed selections (unchanged shape from before sc-1212 round 2) —
  // still shareable via the URL, but no longer sent to the server. See
  // `rowFilters` below, which is what actually filters the rows.
  const urlFilters = React.useMemo(
    () => ({
      experience: parseCsvParam(
        search.experience,
      ) as Array<RollupExperienceType>,
      person: parseCsvParam(search.person),
    }),
    [search.experience, search.person],
  )

  // Fetched ONCE, with no filters — see `EMPTY_QUEUE_FILTERS`'s doc comment.
  //
  // `isLoadingError`, not `isError`: this query polls (sc-1620), and a poll
  // that fails leaves `isError` true while the last good queue is still
  // cached. Passing `isError` to the lists swapped a working queue for
  // "Couldn't load" until the next poll succeeded.
  const { data, isPending, isLoadingError, refetch } =
    useBackofficeAnalyticsForecastQueue(EMPTY_QUEUE_FILTERS)

  // Pushes the schedule dates onto HubSpot tickets now, rather than waiting
  // for the half-hourly sweep (sc-1212). Lives on this page because these
  // are the properties this page describes — not the console's "Resync
  // tickets" button, which crawls every ticket for an unrelated purpose.
  const syncCaseSchedule = useSyncCaseSchedule()
  // Held true for a floor of `SYNC_BUTTON_MIN_BUSY_MS` even after the
  // mutation resolves — see that constant's doc comment for why
  // `syncCaseSchedule.isPending` alone isn't enough.
  const syncButtonBusy = useMinimumPendingDuration(
    syncCaseSchedule.isPending,
    SYNC_BUTTON_MIN_BUSY_MS,
  )

  const setExperience = (values: Array<string>) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        experience: serializeCsvParam(values),
      }),
      replace: true,
    })
  }
  const setPerson = (values: Array<string>) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        person: serializeCsvParam(values),
      }),
      replace: true,
    })
  }

  /**
   * Writes a patch onto the URL's search. ONE `navigate` per user action: two
   * back-to-back functional updates read the same pre-commit location and the
   * second clobbers the first, so handlers that change several params (a
   * selection starting also opens All; Clear filters resets a dozen) build one
   * patch. `undefined` removes the key.
   *
   * `replace` by default: a filter write must not leave a history entry per
   * click. Only a tab or segment change passes `push`, so Back walks the tabs.
   */
  const updateSearch = (patch: Partial<ForecastsSearch>, push = false) => {
    void navigate({
      search: (prev) => ({ ...prev, ...patch }),
      replace: !push,
    })
  }

  // In-browser filter state, all of it in the URL (sc-1212) so a view is
  // shareable, bookmarkable and survives a refresh. None of it ever reaches
  // `useBackofficeAnalyticsForecastQueue`'s query key: each param is a single
  // comma-joined string and the arrays below are memoised on it, so identities
  // stay stable and the queue is still fetched once.
  const searchText = search.q ?? ''
  /**
   * The search box. The URL holds the committed text (`q`); the box holds what
   * is being typed, and a 250ms debounce commits it with `replace`, so typing
   * neither spams history nor re-renders the page per keystroke.
   */
  const searchBox = useDebouncedUrlText(search.q, (value) =>
    updateSearch({ q: value }),
  )
  // Off by default — see `ForecastQueueRowFilters.includeAnnualReviews`.
  const includeAnnualReviews = search.annual === 'on'
  const setIncludeAnnualReviews = (on: boolean) =>
    updateSearch({ annual: on ? 'on' : undefined })
  /**
   * Off by default, and ON replaces the tabs with one Alerts table —
   * see `ForecastViewMode`.
   *
   * **It is a view, not a filter, and the distinction decides three things
   * below.** It is absent from `rowFilters` (the predicates no longer know
   * about alerts at all), absent from `hasActiveFilters` (it narrows nothing
   * — it changes which body is on screen), and untouched by `clearFilters`,
   * so clearing the filters inside the Alerts view leaves you in the Alerts
   * view with every alerted case, which is what a reader means by it. The
   * toggle is the only way back, and the section says so, because the tab
   * strip is hidden while it is on.
   */
  const alertsView = search.alerts === 'on'
  const setAlertsView = (on: boolean) =>
    updateSearch({ alerts: on ? 'on' : undefined })
  // Off by default (empty array = all kinds) — same permissive convention as
  // `caseKinds`, never the Annual-Review-style hide-by-default toggle.
  const caseWorkKinds = React.useMemo(
    () => parseKnownCsv(search.work, FORECAST_WORK_KINDS),
    [search.work],
  )
  const setCaseWorkKinds = (values: ReadonlyArray<CaseWorkKind>) =>
    updateSearch({ work: serializeCsvParam(values) })
  // Off by default (empty array = all reasons) — same permissive convention.
  // Selecting any reason implies "and has no meeting booked", because a banded
  // row carries a null reason and can never match.
  const unscheduledReasons = React.useMemo(
    () => parseKnownCsv(search.reasons, FORECAST_UNSCHEDULED_REASONS),
    [search.reasons],
  )
  const setUnscheduledReasons = (
    values: ReadonlyArray<Exclude<UnscheduledReason, null>>,
  ) => updateSearch({ reasons: serializeCsvParam(values) })
  /**
   * Which list is open (`list`; absent = the default, Urgent). Replaces round
   * 9's "Parked only" toggle, which existed to answer "just show me what I've
   * parked" — a question a tab answers better, and for every list rather than
   * one.
   *
   * The param is `list`, never `tab`: the breadcrumb hands the console this
   * page's whole search, and the console's `tab` is a different enum. See
   * `forecasts-search-schema.ts`.
   */
  const activeList: string = search.list ?? DEFAULT_FORECAST_LIST
  // Off by default (empty array = all planners) — sc-1212 round 15, Tyler:
  // *"maybe use our employee picker component as a filter and set up this tab
  // to use that some how."* A URL param of its own (`planner`), NOT the shared
  // schema's `person`: `person` means the OWNER there, and reusing it for the
  // planner would make one field mean two jobs depending on which control wrote
  // it, which is the exact conflation this round is fixing. The old reason for
  // keeping it page-local was that argument against REUSING a field, not
  // against URL state, and a distinct param resolves it.
  const plannerUserIds = React.useMemo(
    () => parseCsvParam(search.planner),
    [search.planner],
  )
  const setPlannerUserIds = (values: ReadonlyArray<string>) =>
    updateSearch({ planner: serializeCsvParam(values) })
  /**
   * Which problems the Needs Cleaned tab is narrowed to (sc-1212 audit
   * round; `cleaning`). Empty = every kind, the same permissive convention as
   * every other filter here.
   *
   * Its own URL param rather than a reuse of a shared field, for the reason the
   * planner selection is: the base schema is shared with the console, and a
   * control only this tab has must not make one field mean different things on
   * different pages.
   *
   * Read up here rather than inside the section because "Clear filters" and
   * `hasActiveFilters` below both have to reach it — a filter the page's own
   * clear button cannot reset is how a reader gets stranded on an empty list.
   */
  const cleaningKinds = React.useMemo(
    () => parseKnownCsv(search.cleaning, FORECAST_CLEANING_KINDS),
    [search.cleaning],
  )
  /**
   * Which status pills are selected (sc-1212; `status`). Empty = every status,
   * the same permissive convention as every other filter here; a
   * multi-selection is a union.
   *
   * Outside `ForecastQueueRowFilters` for the reason the group chips are: the
   * pill counts must be taken from the rows BEFORE the selection, which a
   * predicate inside `filterForecastLists` would make impossible. Read up here
   * so `hasActiveFilters` and `clearFilters` reach it.
   */
  const statusPills = React.useMemo(
    () => parseKnownCsv(search.status, FORECAST_STATUS_KEYS),
    [search.status],
  )
  /**
   * Which stage pills are selected (sc-1212; `stage`). Empty = every stage; a
   * multi-selection is a union. Case Design stage ids, the vocabulary in
   * `forecast-stage-pills.ts`.
   *
   * Outside `ForecastQueueRowFilters` for the same reason the status pills are:
   * the counts on the row must be taken from the rows BEFORE this selection,
   * which a predicate inside `filterForecastLists` would make impossible. It
   * replaced the `Rework stage` dropdown, which cut the same rows the
   * Revisions and Alt scenarios pills now do.
   */
  const stagePills = React.useMemo(
    () => stageIdsFromParam(search.stage),
    [search.stage],
  )
  /**
   * Which goal shortfalls are selected (sc-1212; `goals`). Empty = no goal
   * filter. Read up here for the same reason the pills are: `hasActiveFilters`
   * and `clearFilters` must reach it, and the goal readings are taken BEFORE it.
   */
  const goalFilters = React.useMemo(
    () => parseKnownCsv(search.goals, FORECAST_GOAL_KEYS),
    [search.goals],
  )
  /**
   * Whether the reader is on the All segment of the strip -- the selection
   * view (see `forecast-selection-view.ts`; `all=1`). Set when a goal or
   * status selection STARTS (empty to non-empty) and cleared when they pick a
   * tab, so a selection always opens on the one table and a tab is a
   * deliberate narrowing. It is a separate flag, not a value of `activeList`,
   * so that `activeList` stays the last real tab and clearing the selection
   * returns the reader to it. Meaningless, and ignored, while nothing is
   * selected.
   *
   * **The start-of-selection transition is computed at the click**
   * (`openAllIfStarting`, which reads `selectionView`), never in an effect: a
   * URL-backed value has no "previous render" to compare against, and the
   * click is the only place the empty-to-non-empty edge is observable.
   */
  const onAll = search.all === 'on'
  /**
   * The tab the flat table is narrowed to (`narrow`), or null for All. While a
   * selection is stored the strip is a FILTER on the table (Tyler: *"use the
   * tabs to filter deeper to reduce the main list"*), so picking Scheduled keeps
   * the one table and shrinks it; it does not open Scheduled's grouped body and
   * does not change `filterScope`. Only the five lists the table draws from can
   * be picked this way; Parked, Billable work, Needs Cleaned and Reference are
   * destinations. Mirrored into `activeList` so clearing the selection lands
   * the reader on the tab they were narrowed to.
   */
  const flatTab: SelectionViewTab | null = search.narrow ?? null
  /**
   * Which groups each work tab's chips have selected (sc-1212, Forecasts
   * restructure; `schedGroups`, `unschedGroups`, `plannerGroups`). Empty =
   * every group, the same permissive convention as every other filter here.
   *
   * Outside `ForecastQueueRowFilters`, for the reason `band` is kept out of it:
   * these cut a tab's already-filtered rows into display groups, and the group
   * counts on the chips must be taken from the rows BEFORE the selection, which
   * a predicate that ran inside `filterForecastLists` would make impossible.
   * They are read up here so `hasActiveFilters` and `clearFilters` can reach
   * them -- a chip the page's own clear button cannot reset is how a reader
   * gets stranded on an empty group.
   */
  const scheduledGroupKeys = React.useMemo(
    () => parseKnownCsv(search.schedGroups, SCHEDULED_GROUP_KEYS),
    [search.schedGroups],
  )
  const unscheduledGroupKeys = React.useMemo(
    () => parseKnownCsv(search.unschedGroups, UNSCHEDULED_GROUP_KEYS),
    [search.unschedGroups],
  )
  const plannerGroupKeys = React.useMemo(
    () => parseKnownCsv(search.plannerGroups, PLANNER_GROUP_KEYS),
    [search.plannerGroups],
  )

  /**
   * Clicking a planner in the billable tab's rollup — the house pattern for a
   * per-person breakdown on this console (`ANALYTICS_DESIGN_STANDARD.md` §9:
   * "By person" is the person-filter control, not a read-only breakdown).
   *
   * Sets the filter to that one person, or clears it if they are already the
   * sole selection, so one click both narrows and un-narrows.
   */
  const togglePlanner = (userId: string) => {
    setPlannerUserIds(
      plannerUserIds.length === 1 && plannerUserIds[0] === userId
        ? []
        : [userId],
    )
  }

  /** One chip click narrows or un-narrows — multi-select, unlike the
   * single-select band tiles, because a cleaning session is usually "all
   * three meeting problems at once" rather than one kind at a time. */
  const toggleCleaningKind = (kind: CleaningFindingKind) => {
    updateSearch({
      cleaning: serializeCsvParam(toggleGroupKey(cleaningKinds, kind)),
    })
  }

  /** The Needs Cleaned meeting-date cutoff; undefined returns to the default (1 Jan), 'all' is no cutoff. */
  const setCleaningSince = (since: string | undefined) =>
    updateSearch({ since })

  const toggleScheduledGroup = (key: ScheduledGroupKey) =>
    updateSearch({
      schedGroups: serializeCsvParam(toggleGroupKey(scheduledGroupKeys, key)),
    })
  const toggleUnscheduledGroup = (key: UnscheduledGroupKey) =>
    updateSearch({
      unschedGroups: serializeCsvParam(
        toggleGroupKey(unscheduledGroupKeys, key),
      ),
    })
  const togglePlannerGroup = (key: PlannerGroupKey) =>
    updateSearch({
      plannerGroups: serializeCsvParam(toggleGroupKey(plannerGroupKeys, key)),
    })

  /**
   * The Needs Cleaned tab's own fetch (sc-1212 audit round).
   *
   * **Its own endpoint with its own server-side paging**, never a slice of
   * the queue payload above: the kinds run to hundreds of rows each, so this
   * one cannot be fetched whole and filtered in the browser the way
   * `EMPTY_QUEUE_FILTERS` lets the queue be.
   *
   * It takes THE PAGE'S OWN controls — the search box, the Owner
   * (specialist) picker and the Planner picker — rather than growing a
   * second set beside them. `search` is debounced inside the use case
   * because here it is a server param, not a client-side row match.
   */
  const cleaning = useCleaningFindings({
    kinds: cleaningKinds,
    ownerIds: urlFilters.person,
    plannerUserIds,
    // The raw box text, not the committed `q`: this hook debounces its own
    // search because here it is a server param, and feeding it the already
    // debounced URL value would stack two delays.
    search: searchBox.value,
    since: effectiveCleaningSince(search.since),
  })

  /**
   * Where the page is, for the purpose of deciding which filters are live:
   * the Alerts view (the tab strip is hidden, the body draws from every work
   * list) or the open tab. See `FILTER_TABS`.
   */
  const selectionView = selectionViewActive({
    alertsView,
    goals: goalFilters,
    pills: statusPills,
    onAll,
  })
  // The Alerts view outranks the selection view (a deliberate mode switch), and
  // with no tab chosen the scope is SELECTION_SCOPE, never `undefined` -- every
  // predicate keyed on a scope has an explicit answer for it.
  const filterScope = alertsView
    ? 'alerts'
    : selectionView
      ? SELECTION_SCOPE
      : activeList

  /**
   * The filters the page is actually applying: every stored selection, with
   * each field BLANKED when the current tab is not one it is live on.
   *
   * **This is what makes hiding a control safe** (`FILTER_TABS`'s invariant:
   * a filter applies exactly where its control is visible). The stored state
   * above is never touched, so a selection survives a trip to a tab where it
   * does not apply, and the URL-backed `experience` / `person` params are
   * never cleared by a tab change.
   *
   * One consequence worth knowing: the other tabs' strip counts and the status
   * pills are computed under THIS tab's effective filters, not under each
   * tab's own.
   */
  const rowFilters = React.useMemo(
    () =>
      effectiveRowFilters(
        {
          ...DEFAULT_FORECAST_QUEUE_ROW_FILTERS,
          search: searchText,
          experiences: urlFilters.experience,
          ownerIds: urlFilters.person,
          includeAnnualReviews,
          caseWorkKinds,
          unscheduledReasons,
          plannerUserIds,
        },
        filterScope,
      ),
    [
      searchText,
      urlFilters.experience,
      urlFilters.person,
      includeAnnualReviews,
      caseWorkKinds,
      unscheduledReasons,
      plannerUserIds,
      filterScope,
    ],
  )

  /**
   * The status pills the page is actually applying: the stored selection on
   * the tabs the pills narrow, nothing elsewhere. Same blanking idea as
   * `rowFilters`, so a selection survives a trip to Billable work or the
   * Alerts view without narrowing a list that has no status to match.
   */
  const activePills = statusPillsApplyOn(filterScope) ? statusPills : []
  /** Goal shortfalls applied: the stored selection on the three tabs in the goal denominator. */
  const activeGoals = goalFilterAppliesOn(filterScope) ? goalFilters : []
  /**
   * The stage pills the page is actually applying: the stored selection on a
   * list the stages can narrow, nothing on Billable work, Needs Cleaned (the
   * pills are inert there, so a stored value must not narrow anything), the
   * Alerts view (the row is hidden) or Reference (no rows on screen).
   */
  const stagePillsLive =
    !alertsView && filterScope !== 'reference' && stagePillsNarrow(filterScope)
  const activeStages = stagePillsLive ? stagePills : []
  /** Delivered rows survive only an empty selection or one holding Ready. */
  const showsDeliveredByStatus = keepsDeliveredRows(activePills)
  /**
   * ...and no stage selection at all. A delivered row sits in Ready for
   * Presentation, which is none of the seven stages, so a row that cannot be
   * in the selected stage must not appear under "show me Planning".
   */
  const showsDeliveredRows =
    showsDeliveredByStatus &&
    activeStages.length === 0 &&
    // Delivered rows are unbooked, so they fall short of neither goal.
    activeGoals.length === 0

  /**
   * Whether anything is NARROWING the rows — which is what the "Clear
   * filters" button promises to undo.
   *
   * **`alertsView` is deliberately not here.** It narrows nothing; it swaps
   * the body. Counting it would put a "Clear filters" button on screen purely
   * because a reader switched view, and clicking it would either do nothing
   * visible or throw them back to the tabs they had just left — the half-state
   * this page has been bitten by before. The toggle is its own undo.
   *
   * **It counts the EFFECTIVE set, not everything stored.** `rowFilters` is
   * already blanked for filters hidden on this tab, and the status pills and each
   * tab's chips count only where they are on screen, so "Clear filters" never
   * appears for a value that is not affecting what the reader sees (a stored
   * planner selection while on Urgent, say). `clearFilters` below is the
   * opposite on purpose: it resets EVERYTHING stored, so switching tabs
   * afterwards cannot reveal a stale value.
   */
  const hasActiveFilters =
    hasActiveRowFilters(rowFilters) ||
    (filterScope === 'cleaning' &&
      (cleaningKinds.length > 0 || search.since !== undefined)) ||
    (filterScope === 'scheduled' && scheduledGroupKeys.length > 0) ||
    (filterScope === 'unscheduled' && unscheduledGroupKeys.length > 0) ||
    (filterScope === 'workDue' && plannerGroupKeys.length > 0) ||
    activePills.length > 0 ||
    activeGoals.length > 0 ||
    activeStages.length > 0

  /**
   * Every working list under the current filters, in ONE pass.
   *
   * This is what both bodies read: the tabs below and the Alerts view
   * that replaces them. It used to be seven near-identical `useMemo`s in this
   * file, which was fine while each list's only reader was its own tab — but
   * an Alerts view that re-derived them off `data` would be a second answer to
   * "what is on this page right now", and the owner picker would appear to
   * work on the tabs and not in the view. See `filterForecastLists`.
   *
   * The status pills are NOT applied here: these are the pre-pill populations
   * the pills count against, and `narrowQueueRowsByPills` narrows them below.
   */
  const lists = React.useMemo(
    () => filterForecastLists(data, rowFilters),
    [data, rowFilters],
  )

  // The turnaround budget the rows were counted against, from the SAME payload
  // (never a second request — see `caseBudgetBusinessDays`). Null on an API
  // older than the field; the section then drops the number rather than
  // inventing one.
  const budgetBusinessDays = caseBudgetBusinessDays(data?.budgetBusinessDays)

  /**
   * The Alerts view's rows — every alerted case across the eight work lists,
   * once each, deduped on `ticketId` with the earliest tab in strip order
   * winning. Built off `lists`, which is why the page's search, owner, planner
   * and Annual Review filters reach it without being re-applied here.
   *
   * Computed whether or not the view is on: it is a pass over ~175 rows
   * already in memory, and the alternative is a conditional hook.
   */
  const alertRows = React.useMemo(() => buildAlertsViewRows(lists), [lists])

  const viewMode = forecastViewMode(alertsView)

  // The billable list takes the three filters that can reach it — see
  // `matchesPostDeliveryWorkFilters` for why Experience and Work kind cannot,
  // and the section's own copy, which says so on screen rather than leaving a
  // reader to decide the pickers are broken.
  //
  // `parked` is left unfiltered for the same reason the other delivered lists
  // leave theirs: it is a disclosure of what a manager has already judged, not
  // a slice of the list above it.
  const postDeliveryWork = React.useMemo(() => {
    const section = data?.postDeliveryWork
    if (!section) return undefined
    return { ...section, rows: lists.postDelivery }
  }, [data?.postDeliveryWork, lists.postDelivery])

  /**
   * Ready to close, filtered like its two sibling Ready-stage lists.
   *
   * **It was reaching the section unfiltered**, which the red-rows toggle made
   * untenable: a filter that narrows seven tabs and not the eighth reads as
   * broken on the ninth. It also contradicted this section's own empty state,
   * which has always offered "try clearing the search, owner, experience, work
   * kind or Annual Review filter" — copy that can only be true if those
   * filters apply. Same predicate as Unscheduled Work's delivered group and Revisions expected, because
   * these are the same row shape drawn from the same Ready population.
   */
  const readyToClose = React.useMemo(() => {
    const section = data?.readyToClose
    if (!section) return undefined
    return { ...section, rows: showsDeliveredRows ? lists.readyToClose : [] }
  }, [data?.readyToClose, lists.readyToClose, showsDeliveredRows])

  // Named for what they are rather than read off `lists` inline: these have
  // passed every filter EXCEPT the status pills, which is the population the
  // pills count against, so their counts stay coherent whichever pills are
  // selected. See `filterForecastLists`.
  //
  // The server still partitions the open queue into `urgent` / `scheduled` /
  // `unscheduled` on `group`, and that cannot change (see
  // `forecast-queue-groups.ts`). The Scheduled and Unscheduled Work tabs are
  // that same queue re-split on whether a meeting is booked, so Urgent's rows
  // appear on Urgent AND on their booking tab -- intended.
  const urgentBeforePills = lists.urgent
  const { booked: bookedBeforePills, unbooked: unbookedBeforePills } =
    React.useMemo(() => queuePopulations(lists), [lists])

  const groupedNow = new Date()

  /**
   * The goal bars read the open queue under `rowFilters` ONLY: no status, stage
   * or goal selection reaches them.
   *
   * **This is deliberately NOT the facet rule the pill rows follow.** A pill
   * count excludes only its own row's selection, so it answers "what would I
   * get if I clicked this, given everything else". A goal's `shortfall` is the
   * headline on a scoreboard and must never move because some other control was
   * clicked (Tyler: "i click 6 then the bottom one changes to show 36, i dont
   * understand this."). `rowFilters` (search, owner, planner, experience) do
   * reach it, because they define WHICH book is being scored and the table the
   * button opens is narrowed by them too, so the number on the button stays the
   * number of rows it produces when no selection is stored. Pinned in
   * `ForecastsPageStagePills.spec.tsx`.
   */
  const goalReadings = React.useMemo(
    () =>
      forecastGoalReadings(
        [...lists.urgent, ...lists.scheduled, ...lists.undated],
        groupedNow,
      ),
    // Same reasoning as `pillCounts`: move with the rows, not the clock.

    [lists],
  )

  // The pills narrow the open queue, Parked included (Tyler asked for the
  // status to be the one filter that reaches every list), and the delivered
  // lists only when Ready is part of the selection.
  const byStatus = (rows: Array<ForecastQueueRow>) =>
    narrowQueueRowsByPills(rows, activePills, groupedNow)
  // ...and then by stage, alongside every other filter. The two tiers are an
  // intersection: a row has to be in a selected status AND a selected stage.
  // ...and a third tier, the goal shortfalls (union within, intersection across).
  const byGoals = (rows: Array<ForecastQueueRow>) =>
    narrowQueueRowsByGoals(rows, activeGoals, groupedNow)
  const byOtherTiers = (rows: Array<ForecastQueueRow>) =>
    narrowRowsByStage(byStatus(rows), activeStages)
  const byPills = (rows: Array<ForecastQueueRow>) => byGoals(byOtherTiers(rows))

  /**
   * The numbers on the status pills: the facet rule. Each count is taken from
   * the open queue narrowed by EVERYTHING except the status selection (the
   * stage selection, the goal selection and `rowFilters` all flow through), so
   * a pill answers "how many would I get by choosing this, given the rest",
   * and a pill's own selection never moves its own number.
   *
   * An earlier version counted before ALL selections ("so a pill reads the same
   * selected or not"). That is right for a pill's OWN selection and wrong for
   * every other control: picking a stage left Queue reading the whole book.
   *
   * The open queue is the server's three disjoint lists, so an urgent case is
   * counted once even though it also shows on its booking tab. Ready is deduped
   * on ticket (three delivered lists, one population) and is zero while a stage
   * or goal is selected, as delivered rows are in neither a stage nor a goal.
   */
  const pillCounts = React.useMemo(() => {
    const queue = narrowRowsByStage(
      byGoals([...lists.urgent, ...lists.scheduled, ...lists.undated]),
      activeStages,
    )
    const delivered =
      activeStages.length === 0 && activeGoals.length === 0
        ? new Set(
            [...lists.awaiting, ...lists.workDue, ...lists.readyToClose].map(
              (row) => row.ticketId,
            ),
          )
        : new Set<string>()
    return statusPillCounts(queue, delivered.size, groupedNow)
    // `groupedNow` moves every render; the counts only need to move when the
    // rows or selections do (a day-boundary crossing is picked up on refetch).
  }, [lists, activeStages, activeGoals])
  /**
   * The selection view's rows: every case the selection matches, once each.
   * Built off `lists` (so the page's row filters reach it) with the tiers the
   * `'all'` scope applies. Computed whether or not the view is on, like
   * `alertRows`: a pass over rows already in memory beats a conditional hook.
   */
  const selectionRows = React.useMemo(
    () =>
      buildSelectionViewRows(
        lists,
        { goals: activeGoals, pills: activePills, stages: activeStages },
        groupedNow,
      ),
    // `groupedNow` moves every render; the rows only need to move with the data.
    [lists, activeGoals, activePills, activeStages],
  )
  // With a goal or status selection stored, a tab reads the rows the flat table
  // shows if that tab is picked. Declared here because the dead end reads it.
  const storedSelection = hasSelection(goalFilters, statusPills)
  const perTabCounts = storedSelection
    ? selectionTabCounts({
        lists,
        stored: { goals: goalFilters, pills: statusPills, stages: stagePills },
        now: groupedNow,
      })
    : null
  // The table's rows once the strip has narrowed it: that one list, as the
  // list holds it (see `buildCaseListRows`'s `only`).
  const narrowedRows = React.useMemo(
    () =>
      flatTab
        ? buildSelectionViewRows(
            lists,
            { goals: activeGoals, pills: activePills, stages: activeStages },
            groupedNow,
            flatTab,
          )
        : selectionRows,
    [lists, activeGoals, activePills, activeStages, flatTab, selectionRows],
  )
  const parkedRows = byPills(lists.parked)
  const urgentRows = byPills(urgentBeforePills)
  const bookedRows = byPills(bookedBeforePills)
  const unbookedRows = byPills(unbookedBeforePills)
  const workDueRows = showsDeliveredRows ? lists.workDue : []

  /**
   * The three grouped tabs.
   *
   * **Counts come from the rows that passed every filter except the chip
   * selection** (the status pills included, since a pill is a filter): a chip
   * then reads the same selected or not, and the chips sum to the list. The
   * grouped rows are what the selection narrows.
   */
  const scheduledGroupOf = (row: ForecastQueueRow) =>
    scheduledGroupFor(row, groupedNow)
  const scheduledCounts = groupCounts(
    bookedRows,
    SCHEDULED_GROUPS,
    scheduledGroupOf,
  )
  const scheduledGrouped = rowsByGroup(
    bookedRows,
    SCHEDULED_GROUPS,
    scheduledGroupOf,
    scheduledGroupKeys,
  )
  const scheduledVisible = scheduledGrouped.flatMap((entry) => entry.rows)

  // Unscheduled Work holds two row shapes: the open queue's unbooked rows (three
  // groups) and the delivered cases awaiting a next meeting (one).
  //
  // **A status selection without Ready empties the delivered group**, because
  // these rows carry no stage and no deadline, and a row that cannot be Open
  // must not show up under "show me Open". Same rule `unscheduledReasons`
  // already states for the mirror case: *"A row WITH a deadline has
  // `unscheduledReason === null` and so can never match a non-empty
  // selection."*
  //
  // NOT the Parked exemption, which looks similar and is not: Parked is a
  // whole tab a reader opens knowing it is unfiltered, so leaving its rows in
  // hides nothing. This is one group among four inside a list the reader has
  // just narrowed, and leaving it unnarrowed would put a filtered and an
  // unfiltered population under one total.
  const awaitingRows = showsDeliveredRows ? lists.awaiting : []
  const unscheduledCounts: Record<UnscheduledGroupKey, number> = {
    ...groupCounts(unbookedRows, UNSCHEDULED_GROUPS, unscheduledGroupFor),
    DELIVERED_NOTHING_NEXT: awaitingRows.length,
  }
  const unscheduledGrouped = rowsByGroup(
    unbookedRows,
    UNSCHEDULED_GROUPS,
    unscheduledGroupFor,
    unscheduledGroupKeys,
  )
  const unscheduledQueueVisible = unscheduledGrouped.flatMap(
    (entry) => entry.rows,
  )
  const showsDelivered = unscheduledGrouped.some(
    (entry) => entry.group.key === 'DELIVERED_NOTHING_NEXT',
  )
  const unscheduledTotal =
    unscheduledQueueVisible.length + (showsDelivered ? awaitingRows.length : 0)
  const awaitingNextMeeting = React.useMemo(() => {
    const section = data?.awaitingNextMeeting
    if (!section) return undefined
    return { ...section, rows: awaitingRows }
  }, [data?.awaitingNextMeeting, awaitingRows])
  const unscheduledSectionGroups: Array<ForecastQueueSectionGroup> =
    unscheduledGrouped.map((entry) =>
      entry.group.key === 'DELIVERED_NOTHING_NEXT'
        ? {
            ...entry,
            body: (
              <AwaitingNextMeetingSection
                embedded
                data={awaitingNextMeeting}
                closeOut={data?.readyCloseOut}
                isPending={isPending}
                isError={isLoadingError}
                onRetry={() => void refetch()}
                hasActiveFilters={hasActiveFilters}
              />
            ),
            bodyCount: awaitingRows.length,
          }
        : entry,
    )

  // Awaiting planner, grouped by the planner-response state.
  const plannerCounts = groupCounts(
    workDueRows,
    PLANNER_GROUPS,
    plannerGroupFor,
  )
  const plannerGrouped = rowsByGroup(
    workDueRows,
    PLANNER_GROUPS,
    plannerGroupFor,
    plannerGroupKeys,
  )
  // Rows in no planner group (null: no clock; absent: older API). Shown last,
  // and only while no chip is selected, so a chip still narrows to its state.
  const plannerUngraded =
    plannerGroupKeys.length === 0
      ? workDueRows.filter((row) => plannerGroupFor(row) === null)
      : []
  const plannerVisible = [
    ...plannerGrouped.flatMap((entry) => entry.rows),
    ...plannerUngraded,
  ]
  const upcomingDeliveryWork = data?.upcomingDeliveryWork
    ? { ...data.upcomingDeliveryWork, rows: plannerVisible }
    : undefined

  /**
   * The numbers on the stage pills: the facet rule, as for the status pills.
   * The open queue narrowed by everything except the STAGE selection (status,
   * goals and `rowFilters` flow through), so choosing a stage never moves the
   * stage row's own numbers but does move the status row's.
   */
  const stageCounts = React.useMemo(
    () =>
      stagePillCounts(
        byGoals(
          byStatus([...lists.urgent, ...lists.scheduled, ...lists.undated]),
        ),
      ),

    [lists, activePills, activeGoals],
  )
  const toggleStage = (stageId: string) =>
    updateSearch({
      stage: stageParamFromIds(toggleGroupKey(stagePills, stageId)),
    })

  // A selection STARTING opens the one table (All); later toggles leave the
  // reader wherever they are, including on a tab they chose to narrow to.
  //
  // Computed HERE, at the click, and returned as a patch for the SAME navigate
  // as the selection change (one write, one history replace). The edge it
  // detects (not in the table yet) is only observable now: `all` and `narrow`
  // are URL values with no previous render to compare against.
  const openAllPatchIfStarting = (): Partial<ForecastsSearch> =>
    // The controls render on every tab and a click opens the page-wide table
    // wherever the reader stands, so a click from anywhere but the table (a
    // destination tab, say) takes them to it. Once they are IN the table,
    // toggles leave the tab they narrowed to alone.
    selectionView ? {} : { all: 'on', narrow: undefined }
  const toggleGoal = (key: ForecastGoalKey) =>
    updateSearch({
      ...openAllPatchIfStarting(),
      goals: serializeCsvParam(toggleGoalFilter(goalFilters, key)),
    })
  const togglePill = (key: ForecastStatusPillKey) =>
    updateSearch({
      ...openAllPatchIfStarting(),
      status: serializeCsvParam(toggleStatusPill(statusPills, key)),
    })
  /** Jumps to a list from a dead end: a tab change, so it pushes. */
  const goToList = (tab: string) =>
    updateSearch({ list: listParamFor(tab) }, true)

  /**
   * Where a reader goes when the pills have emptied their tab.
   *
   * Counted per tab UNDER the status selection but BEFORE any group-chip
   * selection, so a tab emptied by its own chips is not blamed on a pill.
   * `unscheduled` includes its delivered group, as its strip count does.
   *
   * **Status only, not the stage selection.** The jump's copy says "has N that
   * are Behind schedule", which would be false if N also had to be in the selected
   * stage. A tab emptied by a stage pill alone is not a status dead end, and
   * falls through to the section's own empty state and Clear filters.
   */
  const pillRowsByTab: Record<string, number> = {
    urgent: byStatus(urgentBeforePills).length,
    scheduled: byStatus(bookedBeforePills).length,
    unscheduled:
      byStatus(unbookedBeforePills).length +
      (showsDeliveredByStatus ? lists.awaiting.length : 0),
    workDue: showsDeliveredByStatus ? lists.workDue.length : 0,
    readyToClose: showsDeliveredByStatus ? lists.readyToClose.length : 0,
    parked: byStatus(lists.parked).length,
  }
  /**
   * Goal dead end: counted per goal-scoped tab under the goal selection AND the
   * status selection (stage and group chips excluded, as above). A separate
   * helper from the status one because its copy names a goal; the two never
   * both show: with a goal selected, this one answers, since the status count
   * alone could point at a tab the goal then empties.
   */
  const goalRowsByTab: Record<string, number> = {
    urgent: byGoals(byStatus(urgentBeforePills)).length,
    scheduled: byGoals(byStatus(bookedBeforePills)).length,
    unscheduled: byGoals(byStatus(unbookedBeforePills)).length,
  }
  const goalEnd = isPending
    ? null
    : goalDeadEnd(activeList, goalRowsByTab, activeGoals)
  const deadEnd =
    isPending || activeGoals.length > 0
      ? null
      : statusPillDeadEnd(activeList, pillRowsByTab, activePills)
  /**
   * The flat table's dead end: the strip narrowed it to a tab the selection
   * leaves empty. Counts are the strip's own, so the jump names a tab that
   * really holds rows.
   */
  const flatEnd =
    selectionView && flatTab && perTabCounts && !isPending
      ? selectionDeadEnd(flatTab, perTabCounts)
      : null
  /** The section for `tab`, or the dead-end state when a pill or goal emptied it. */
  const orDeadEnd = (tab: string, section: React.ReactNode) =>
    goalEnd && goalEnd.from === tab ? (
      <StatusPillDeadEndState
        deadEnd={goalEnd}
        selected={[]}
        selectionLabel={`short of ${goalSelectionLabel(activeGoals)}`}
        clearLabel="Clear goal"
        onGo={goToList}
        onClear={() => updateSearch({ goals: undefined })}
      />
    ) : deadEnd && deadEnd.from === tab ? (
      <StatusPillDeadEndState
        deadEnd={deadEnd}
        selected={activePills}
        onGo={goToList}
        onClear={() => updateSearch({ status: undefined })}
      />
    ) : (
      section
    )

  /**
   * Switches between the tabs and the single Alerts table.
   *
   * The status pills are hidden in the Alerts view and do not narrow it (it
   * holds closed cases with no status), so the stored selection is kept for
   * the way back rather than cleared.
   */
  /**
   * The number beside each tab, of rows passing the CURRENT filters.
   *
   * Filtered rather than server totals, for the same reason the status pills
   * are: a tab reading 42 that opens onto 9 rows is worse than no number.
   * The two delivered lists count their visible rows only — their parked rows
   * are inside a disclosure of their own and are not what the label promises.
   */
  // With a goal or status selection stored, a tab reads the rows the flat table
  // shows if that tab is picked (Parked, a destination, reads its own body).
  // Without one the counts below are exactly what they always were.
  const queueTabCounts: Record<string, number | null> = isPending
    ? // Null, not 0, while the queue is loading. Zero is a CLAIM -- "there is
      // nothing urgent" is the best news on this page -- and showing it for a
      // second before the real number lands is the kind of flicker a reader
      // learns to distrust.
      Object.fromEntries(LIST_TABS.map((tab) => [tab.value, null]))
    : perTabCounts
      ? {
          ...perTabCounts,
          postDelivery: postDeliveryWork?.rows.length ?? 0,
        }
      : {
          urgent: urgentRows.length,
          // Rows currently shown, after the chip selection: a tab reading 28 that
          // opens onto 3 rows is the contradiction these counts exist to avoid.
          scheduled: scheduledVisible.length,
          unscheduled: unscheduledTotal,
          workDue: upcomingDeliveryWork?.rows.length ?? 0,
          parked: parkedRows.length,
          readyToClose: readyToClose?.rows.length ?? 0,
          // `?? 0` carries real weight here, not just a default: `apps/web` and
          // `apps/api` deploy separately, so this field can be absent from a
          // payload whose type says it is required. An absent section reads as an
          // empty list, never as a crash.
          postDelivery: postDeliveryWork?.rows.length ?? 0,
        }

  /**
   * Needs Cleaned counts off its OWN query, so a slow forecast-queue fetch
   * does not hold its number hostage and vice versa.
   *
   * `total` (the server's count under the current filters), not
   * `rows.length` — every other tab here filters a payload it already holds,
   * so its visible rows ARE its count, but this one shows 50 of a total that
   * was 653 on 2026-10-08 and is not a constant.
   * A tab reading 50 over a pager saying "Page 1 of 12" would be the same
   * contradiction §16c had to caption its way out of.
   *
   * Null while that query has never resolved, for the same reason the queue's
   * are: zero problems is the best news on this tab, and flashing it before
   * the real number lands teaches a reader to distrust the label.
   */
  const tabCounts: Record<string, number | null> = {
    ...queueTabCounts,
    cleaning: cleaning.isPending ? null : cleaning.total,
    // The All segment's count is the table's: rows of the selection view.
    [SELECTION_SCOPE]: isPending ? null : selectionRows.length,
  }

  /**
   * Clears EVERY stored filter, not just the ones live on this tab, so
   * switching tabs afterwards cannot reveal a stale value the reader never
   * saw (`hasActiveFilters` is the visible-only counterpart). Clears the
   * filters and NOTHING ELSE — in particular it does not leave the
   * Alerts view (see `hasActiveFilters`). Inside that view, clearing the
   * filters widens it to every alerted case, which is what a reader who
   * clicked it there meant.
   */
  const clearFilters = () => {
    // The box holds its own text, so it has to be emptied too; the URL's `q`
    // is cleared by the same single write below (this commit is a no-op echo).
    searchBox.setValue('')
    // ONE navigate for everything: sequential functional updates would each
    // read the pre-commit location and the last one would win. `all`,
    // `narrow`, `list` and `alerts` are deliberately absent -- they are where
    // the reader is, not what is narrowing the rows.
    updateSearch({
      q: undefined,
      annual: undefined,
      work: undefined,
      stage: undefined,
      reasons: undefined,
      planner: undefined,
      cleaning: undefined,
      since: undefined,
      schedGroups: undefined,
      unschedGroups: undefined,
      plannerGroups: undefined,
      status: undefined,
      goals: undefined,
      experience: undefined,
      person: undefined,
    })
  }

  const additionalFilterChips = [
    ...rowFilters.caseWorkKinds.map((kind) => ({
      label: ANALYTICS_WORK_KIND_LABELS[kind],
      clear: () =>
        setCaseWorkKinds(caseWorkKinds.filter((value) => value !== kind)),
    })),
    ...rowFilters.unscheduledReasons.map((reason) => ({
      label: UNSCHEDULED_REASON_LABELS[reason],
      clear: () =>
        setUnscheduledReasons(
          unscheduledReasons.filter((value) => value !== reason),
        ),
    })),
    ...activeStages.map((id) => ({
      label: CASE_DESIGN_STAGES.find((stage) => stage.id === id)?.label ?? id,
      clear: () => toggleStage(id),
    })),
    ...(rowFilters.includeAnnualReviews
      ? [
          {
            label: 'Annual Reviews included',
            clear: () => setIncludeAnnualReviews(false),
          },
        ]
      : []),
  ]
  const additionalFilterCount = additionalFilterChips.length

  const selectList = (next: string) => {
    if (storedSelection && next === SELECTION_SCOPE) {
      updateSearch({ all: 'on', narrow: undefined }, true)
    } else if (storedSelection && isSelectionViewTab(next)) {
      updateSearch({ all: 'on', narrow: next, list: listParamFor(next) }, true)
    } else {
      updateSearch({ all: undefined, list: listParamFor(next) }, true)
    }
  }

  return (
    <div className="space-y-4 py-4">
      <PageTitle title="Scheduled Work" />

      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              {/* Carries the console's whole search state back — the reader
                  returns to exactly the tab/filters they left, not a reset
                  default (see `analytics-search-schema.ts`'s doc comment). */}
              <Link to="/backoffice/analytics" search={search}>
                Analytics
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Scheduled Work</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
              Scheduled Work
            </h1>
            <InfoHint
              label="About this work queue"
              className="relative before:absolute before:-inset-3.5 sm:before:hidden"
            >
              A snapshot of open service work. Meetings set deadlines. Search
              and filters apply to the current view; status and goal selections
              open a list across queues. Needs Cleaned is paged separately.
            </InfoHint>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Service tickets, prioritized by delivery deadline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            aria-label="Sync schedule to HubSpot"
            className="h-11 sm:h-8"
            disabled={syncButtonBusy}
            onClick={() => syncCaseSchedule.mutate()}
          >
            {syncButtonBusy ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="mr-1.5 size-3.5" aria-hidden />
            )}
            <span className="hidden sm:inline">Sync schedule</span>
            <span className="sm:hidden">Sync</span>
          </Button>
          {data && (
            <p className="hidden text-xs tabular-nums text-muted-foreground lg:block">
              Updated {formatDateTime(data.generatedAt)}
            </p>
          )}
        </div>
      </div>

      {/* This is a standing snapshot, not a windowed metric (sc-1212) — the
          API deliberately ignores the console's date Range, so this page
          shows no Range control at all rather than one that would silently
          do nothing. */}
      {showsListTabs(viewMode) && (
        <ForecastGoalBars
          readings={isPending ? undefined : goalReadings}
          isLoading={isPending}
          selected={goalFilters}
          onToggle={toggleGoal}
        />
      )}
      <div className="space-y-2" aria-label="Case filters">
        <div className="flex flex-wrap items-center gap-2">
          {filterAppliesOn('search', filterScope) && (
            <div className="relative w-full sm:w-64">
              <Label className="sr-only" htmlFor="forecasts-search">
                Search
              </Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="forecasts-search"
                  value={searchBox.value}
                  onChange={(event) => searchBox.setValue(event.target.value)}
                  placeholder="Search cases…"
                  className="h-11 pl-8 pr-11 text-base sm:h-8 sm:pr-8 sm:text-sm"
                />
                {searchBox.value && (
                  <button
                    type="button"
                    onClick={() => searchBox.setImmediate('')}
                    aria-label="Clear search"
                    className="absolute right-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground sm:right-2 sm:size-4"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                )}
              </div>
            </div>
          )}

          {filterAppliesOn('experiences', filterScope) && (
            <div className="min-w-0 flex-1 basis-36 sm:w-40 sm:flex-none">
              <Label className="sr-only" htmlFor="forecasts-experience">
                Experience
              </Label>
              <MultiSelectFilter
                id="forecasts-experience"
                options={EXPERIENCE_OPTIONS}
                value={urlFilters.experience}
                onChange={setExperience}
                itemNamePlural="experiences"
                searchPlaceholder="Search experiences…"
                ariaLabel="Filter by experience"
                className="h-11 w-full sm:h-8"
              />
            </div>
          )}
          {filterAppliesOn('ownerIds', filterScope) && (
            <div className="min-w-0 flex-1 basis-40 sm:w-48 sm:flex-none">
              <Label className="sr-only" htmlFor="forecasts-person">
                Owner (specialist)
              </Label>
              <UserPicker
                multiple
                staffOnly
                triggerIcon={ChevronDown}
                triggerLabelPrefix="Owner"
                id="forecasts-person"
                value={urlFilters.person}
                onChange={setPerson}
                placeholder="All specialists"
                className="h-11 w-full sm:h-8"
              />
            </div>
          )}
          {filterAppliesOn('plannerUserIds', filterScope) && (
            <div className="min-w-0 flex-1 basis-40 sm:w-48 sm:flex-none">
              <Label className="sr-only" htmlFor="forecasts-planner">
                Planner
              </Label>
              <UserPicker
                multiple
                staffOnly
                triggerIcon={ChevronDown}
                triggerLabelPrefix="Planner"
                id="forecasts-planner"
                value={plannerUserIds}
                onChange={setPlannerUserIds}
                placeholder="All planners"
                className="h-11 w-full sm:h-8"
              />
            </div>
          )}
          {filterScope !== 'reference' && (
            <ForecastFiltersPopover count={additionalFilterCount}>
              {filterAppliesOn('caseWorkKinds', filterScope) && (
                <div className="w-full space-y-1.5">
                  <Label htmlFor="forecasts-work-kind">Work kind</Label>
                  <MultiSelectFilter
                    id="forecasts-work-kind"
                    options={CASE_WORK_KIND_OPTIONS}
                    value={caseWorkKinds}
                    onChange={(values) =>
                      setCaseWorkKinds(values as Array<CaseWorkKind>)
                    }
                    itemNamePlural="kinds"
                    searchPlaceholder="Search work kind…"
                    ariaLabel="Filter by work kind"
                    className="h-11 w-full sm:h-8"
                  />
                </div>
              )}
              {filterAppliesOn('unscheduledReasons', filterScope) && (
                <div className="w-full space-y-1.5">
                  <Label htmlFor="forecasts-unscheduled-reason">
                    No meeting booked
                  </Label>
                  <MultiSelectFilter
                    id="forecasts-unscheduled-reason"
                    options={UNSCHEDULED_REASON_OPTIONS}
                    value={unscheduledReasons}
                    onChange={(values) =>
                      // Same cast-at-the-boundary as `setCaseWorkKinds` above:
                      // MultiSelectFilter is string-typed, and the only values it
                      // can emit are the option values it was given.
                      setUnscheduledReasons(
                        values as Array<Exclude<UnscheduledReason, null>>,
                      )
                    }
                    itemNamePlural="reasons"
                    searchPlaceholder="Search reason…"
                    ariaLabel="Filter by why no meeting is booked"
                    className="h-11 w-full sm:h-8"
                  />
                </div>
              )}
              {filterAppliesOn('includeAnnualReviews', filterScope) && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="forecasts-include-ar"
                    size="sm"
                    checked={includeAnnualReviews}
                    onCheckedChange={setIncludeAnnualReviews}
                  />
                  <Label
                    htmlFor="forecasts-include-ar"
                    className="inline-flex min-h-11 items-center text-sm font-normal sm:min-h-8"
                  >
                    Include Annual Reviews
                  </Label>
                </div>
              )}
              {showsListTabs(viewMode) && (
                <div className="space-y-2">
                  <h3 className="text-xs font-medium">Stage</h3>
                  <ForecastStagePills
                    selected={stagePills}
                    counts={stageCounts}
                    isLoading={isPending}
                    narrows={stagePillsNarrow(
                      selectionView ? SELECTION_SCOPE : activeList,
                    )}
                    onToggle={toggleStage}
                  />
                </div>
              )}
            </ForecastFiltersPopover>
          )}
          <Button
            type="button"
            size="sm"
            variant={alertsView ? 'secondary' : 'ghost'}
            className="h-11 gap-1.5 text-xs sm:h-8"
            aria-pressed={alertsView}
            onClick={() => setAlertsView(!alertsView)}
          >
            <AlertTriangle className="size-3.5" aria-hidden />
            Alerts view
          </Button>
          {alertsView && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-11 text-xs sm:h-8"
              onClick={() => setAlertsView(false)}
            >
              Work lists
            </Button>
          )}
          {hasActiveFilters && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearFilters}
              className="h-11 text-xs sm:h-8"
            >
              <X className="size-3.5" aria-hidden />
              Clear filters
            </Button>
          )}
        </div>
        {additionalFilterChips.length > 0 && (
          <div
            className="flex flex-wrap gap-1"
            aria-label="Applied additional filters"
          >
            {additionalFilterChips.map((chip) => (
              <Button
                key={chip.label}
                type="button"
                size="sm"
                variant="outline"
                className="h-11 max-w-full gap-1 text-xs sm:h-7"
                onClick={chip.clear}
                aria-label={`Remove ${chip.label}`}
              >
                <span className="truncate">{chip.label}</span>
                <X className="size-3" aria-hidden />
              </Button>
            ))}
          </div>
        )}
      </div>

      {showsListTabs(viewMode) && (
        <ForecastStatusCards
          selected={statusPills}
          counts={pillCounts}
          isLoading={isPending}
          onToggle={togglePill}
        />
      )}

      {/* THE ALERTS VIEW REPLACES THE WHOLE TAB STRIP (sc-1212 red rows,
              round 2). Tyler asked what the tabs should do while it is on:
              hide them. So this is not a tenth `TabsContent` — the `Tabs`
              element itself is out of the tree, which is the only way the
              strip, its counts and the bodies all go at once.

              `activeList` is deliberately NOT reset on the way in or out: a
              reader who was on Revisions expected and looks at the alerts expects Work
              due back when they switch off, and the tab choice narrows
              nothing while the view is on. */}
      {showsAlertsTable(viewMode) ? (
        <AlertsViewSection
          rows={alertRows}
          isPending={isPending}
          isError={isLoadingError}
          onRetry={() => void refetch()}
          hasActiveFilters={hasActiveFilters}
          budgetBusinessDays={budgetBusinessDays}
          requiredReadyDays={requiredReadyBusinessDays(budgetBusinessDays)}
        />
      ) : (
        <>
          {/* ONE TAB PER LIST (sc-1212 round 11, Tyler: "instead of
              stacking and making groups on groups we made professional tabs
              so I don't have to scroll so much").
              
              Flat, with no nesting: every list is one click away and the
              counts live in the labels, so the shape of the whole book is
              readable without opening anything. Stacked, reading the last
              number meant scrolling past four tables.
              
              The open tab is the `list` URL param (and `all` / `narrow` for the
              selection view), PUSHED so Back walks the tabs. It is a param of
              the Forecasts schema, which EXTENDS the console's, so the shared
              contract is not widened; and it is `list`, never `tab`, because
              the breadcrumb hands the console this page's whole search. */}
          <Tabs
            value={selectionView ? (flatTab ?? SELECTION_SCOPE) : activeList}
            onValueChange={selectList}
          >
            <ForecastListNavigation
              storedSelection={storedSelection}
              counts={tabCounts}
            />

            {selectionView ? (
              <TabsContent value={flatTab ?? SELECTION_SCOPE}>
                {flatEnd ? (
                  <StatusPillDeadEndState
                    deadEnd={flatEnd}
                    selected={[]}
                    selectionLabel={selectionHeading(goalFilters, statusPills)}
                    clearLabel="Clear selection"
                    onGo={(tab) => {
                      if (isSelectionViewTab(tab)) {
                        updateSearch(
                          { narrow: tab, list: listParamFor(tab) },
                          true,
                        )
                      }
                    }}
                    onClear={() =>
                      updateSearch({ goals: undefined, status: undefined })
                    }
                  />
                ) : (
                  <SelectionViewSection
                    heading={
                      selectionHeading(goalFilters, statusPills) +
                      (flatTab ? ` on ${LIST_TAB_LABELS[flatTab]}` : '')
                    }
                    rows={narrowedRows}
                    showSource={flatTab === null}
                    budgetBusinessDays={budgetBusinessDays}
                    requiredReadyDays={requiredReadyBusinessDays(
                      budgetBusinessDays,
                    )}
                    isPending={isPending}
                    isError={isLoadingError}
                    onRetry={() => void refetch()}
                    onClearFilters={clearFilters}
                    clearHint={filterClearHint(SELECTION_SCOPE, [
                      'status or goal',
                    ])}
                  />
                )}
              </TabsContent>
            ) : (
              <>
                <TabsContent value="urgent">
                  {orDeadEnd(
                    'urgent',
                    <ForecastQueueSection
                      title="Urgent — past the promised date"
                      description="Past the date it was promised and still open — the morning list, soonest overdue first."
                      rows={data ? urgentRows : undefined}
                      total={data ? urgentRows.length : undefined}
                      isPending={isPending}
                      isError={isLoadingError}
                      onRetry={() => void refetch()}
                      budgetBusinessDays={budgetBusinessDays}
                      variant="urgent"
                      emptyTitle={
                        hasActiveFilters
                          ? 'No cases match these filters'
                          : 'Nothing urgent'
                      }
                      emptyDescription={
                        hasActiveFilters
                          ? `${filterClearHint('urgent', ['status', 'stage'])} Annual Reviews are hidden by default.`
                          : 'No open case has missed the date it was promised.'
                      }
                      hasActiveFilters={hasActiveFilters}
                      onClearFilters={clearFilters}
                      headerExtra={
                        <ForecastOffsetsHint
                          workEstimates={data?.workEstimates}
                        />
                      }
                    />,
                  )}
                </TabsContent>

                <TabsContent value="scheduled">
                  {orDeadEnd(
                    'scheduled',
                    <ForecastQueueSection
                      title="Scheduled"
                      description="A delivery meeting is booked, so the job is to have the plan ready in time. Grouped by how late the work is against that meeting, soonest meeting first."
                      rows={data ? scheduledVisible : undefined}
                      total={data ? scheduledVisible.length : undefined}
                      groups={scheduledGrouped}
                      isPending={isPending}
                      isError={isLoadingError}
                      onRetry={() => void refetch()}
                      budgetBusinessDays={budgetBusinessDays}
                      variant="scheduled"
                      emptyTitle={
                        hasActiveFilters
                          ? 'No cases match these filters'
                          : 'Nothing scheduled'
                      }
                      emptyDescription={
                        hasActiveFilters
                          ? `${filterClearHint('scheduled', ['status', 'stage', 'group'])} Annual Reviews are hidden by default.`
                          : 'No open case has a delivery meeting booked.'
                      }
                      hasActiveFilters={hasActiveFilters}
                      onClearFilters={clearFilters}
                      headerExtra={
                        <>
                          <ForecastOffsetsHint
                            workEstimates={data?.workEstimates}
                          />
                          <ForecastGroupChips
                            groups={SCHEDULED_GROUPS}
                            selected={scheduledGroupKeys}
                            counts={scheduledCounts}
                            isLoading={isPending}
                            onToggle={toggleScheduledGroup}
                            ariaLabel="Filter Scheduled by group"
                          />
                        </>
                      }
                    />,
                  )}
                </TabsContent>

                <TabsContent value="unscheduled">
                  {orDeadEnd(
                    'unscheduled',
                    <ForecastQueueSection
                      title="Unscheduled Work"
                      description="Nothing is on the calendar, so the job is to book a meeting. Grouped by who has to act and how late they are."
                      rows={data ? unscheduledQueueVisible : undefined}
                      total={data ? unscheduledTotal : undefined}
                      groups={unscheduledSectionGroups}
                      isPending={isPending}
                      isError={isLoadingError}
                      onRetry={() => void refetch()}
                      budgetBusinessDays={budgetBusinessDays}
                      variant="unscheduled"
                      emptyTitle={
                        hasActiveFilters
                          ? 'No cases match these filters'
                          : 'Nothing unscheduled'
                      }
                      emptyDescription={
                        hasActiveFilters
                          ? `${filterClearHint('unscheduled', ['status', 'stage', 'group'])} Annual Reviews are hidden by default.`
                          : 'Every open case has a delivery meeting booked, and every delivered case has a next one.'
                      }
                      hasActiveFilters={hasActiveFilters}
                      onClearFilters={clearFilters}
                      headerExtra={
                        <ForecastGroupChips
                          groups={UNSCHEDULED_GROUPS}
                          selected={unscheduledGroupKeys}
                          counts={unscheduledCounts}
                          isLoading={isPending}
                          onToggle={toggleUnscheduledGroup}
                          ariaLabel="Filter Unscheduled Work by group"
                        />
                      }
                    />,
                  )}
                </TabsContent>

                <TabsContent value="workDue">
                  {orDeadEnd(
                    'workDue',
                    <UpcomingDeliveryWorkSection
                      data={upcomingDeliveryWork}
                      isPending={isPending}
                      isError={isLoadingError}
                      onRetry={() => void refetch()}
                      hasActiveFilters={hasActiveFilters}
                      groups={plannerGrouped}
                      ungradedRows={plannerUngraded}
                      groupChips={
                        <ForecastGroupChips
                          groups={PLANNER_GROUPS}
                          selected={plannerGroupKeys}
                          counts={plannerCounts}
                          isLoading={isPending}
                          onToggle={togglePlannerGroup}
                          ariaLabel="Filter Awaiting planner by state"
                        />
                      }
                    />,
                  )}
                </TabsContent>

                <TabsContent value="readyToClose">
                  {orDeadEnd(
                    'readyToClose',
                    <ReadyToCloseSection
                      data={readyToClose}
                      isPending={isPending}
                      isError={isLoadingError}
                      onRetry={() => void refetch()}
                      hasActiveFilters={hasActiveFilters}
                    />,
                  )}
                </TabsContent>

                <TabsContent value="postDelivery">
                  <PostDeliveryWorkSection
                    data={postDeliveryWork}
                    isPending={isPending}
                    isError={isLoadingError}
                    onRetry={() => void refetch()}
                    hasActiveFilters={hasActiveFilters}
                    plannerUserIds={plannerUserIds}
                    onPlannerFilterToggle={togglePlanner}
                  />
                </TabsContent>

                <TabsContent value="parked">
                  {orDeadEnd(
                    'parked',
                    <ForecastQueueSection
                      title="Parked — nothing we can do yet"
                      description="Cases a manager has taken out of the other lists until a date or their next meeting. They return on their own if the stage moves, work starts, or the promised date changes."
                      rows={data ? parkedRows : undefined}
                      total={data ? parkedRows.length : undefined}
                      isPending={isPending}
                      isError={isLoadingError}
                      onRetry={() => void refetch()}
                      budgetBusinessDays={budgetBusinessDays}
                      variant="parked"
                      emptyTitle={
                        hasActiveFilters
                          ? 'No cases match these filters'
                          : 'Nothing parked'
                      }
                      emptyDescription={
                        hasActiveFilters
                          ? `${filterClearHint('parked', ['status', 'stage'])} Annual Reviews are hidden by default.`
                          : 'No case has been parked out of the other lists.'
                      }
                      hasActiveFilters={hasActiveFilters}
                      onClearFilters={clearFilters}
                    />,
                  )}
                </TabsContent>

                <TabsContent value="cleaning">
                  <NeedsCleanedSection
                    view={cleaning}
                    selectedKinds={cleaningKinds}
                    onToggleKind={toggleCleaningKind}
                    since={search.since}
                    onSinceChange={setCleaningSince}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={clearFilters}
                  />
                </TabsContent>

                {/* Everything explanatory, out of the working lists entirely —
                the tabs above are rows and nothing else.

                `ForecastReferenceTab` holds it: the routine, one section per
                tab built off the shared `LIST_TABS`, then the live
                diagnostics. It lives in `-components/` rather than here
                because this file is already 1,100 lines and that panel is
                pure copy plus four numbers off the payload. */}
                <TabsContent value="reference">
                  <ForecastReferenceTab data={data} />
                </TabsContent>
              </>
            )}
          </Tabs>
        </>
      )}
    </div>
  )
}
